import React, { useState, useRef, useEffect } from 'react';
import { motion, AnimatePresence, useMotionValue, useTransform } from 'framer-motion';
import './PricingSection.css';
import useTranslation from '../hooks/useTranslation';
import { createPayPalPayment } from './api';

const TABS = [
  { label: 'Individual', icon: 'user', key: 'individual' },
  { label: 'Doctor', icon: 'doctor', key: 'doctor' },
  { label: 'Corporate', icon: 'corporate', key: 'corporate' },
  { label: 'School / University', icon: 'school', key: 'school' },
  { label: 'Sports Management', icon: 'basketball', key: 'sports' },
  { label: 'Add-ons for All Plans', icon: 'plus', key: 'addons' },
];

const PLANS = [
  {
    name: 'Free Plan',
    price: '$0',
    features: ['Weekly emotional/mental state reports', 'Basic quizzes', 'Basic mood tracker'],
    extra: '',
  },
  {
    name: 'Premium Plan',
    price: '$100/year or $10',
    period: '/month',
    features: ['Monthly & Yearly emotion trends', 'Mental health dashboard', 'Unlimited emotional tracking', 'Sub-sub emotion insights', 'No ads'],
    extra: '',
  },
  {
    name: 'Companion AI',
    price: '+$25',
    period: '/month',
    features: ['Speech-to-speech real-time AI chat', 'Personality customization (virtual companion)', 'Monthly data sync'],
    extra: '',
  },
  {
    name: 'Donation Option',
    price: 'Custom',
    period: '',
    features: ['Pay-what-you-want to support mental health access for low-income individuals'],
    extra: '',
  },
];

const CORPORATE_PLANS = [
  {
    name: 'Team Starter (up to 10 employees)',
    price: '$79',
    period: '/month',
    features: ['Basic mental health insights', 'Mood trends', 'Employee support channel'],
    extra: '',
  },
  {
    name: 'Growth Plan (up to 50)',
    price: '$249',
    period: '/month',
    features: ['Monthly/yearly mental health data', 'Group mood heatmaps', 'Productivity correlation insights'],
    extra: '',
  },
  {
    name: 'Enterprise (100+ employees)',
    price: 'Custom Pricing',
    period: '(starts at $499/month)',
    features: ['Full integration', 'Custom onboarding', 'Mental health workshops', 'Partner psychologist access'],
    extra: '',
  },
];

const DOCTOR_PLANS = [
  {
    name: 'Basic Doctor Dashboard',
    price: '$29',
    period: '/month',
    features: ['Add & manage patient list', 'Weekly reports per patient', 'Basic analytics'],
    extra: '',
  },
  {
    name: 'Advanced Doctor Insights',
    price: '$59',
    period: '/month',
    features: ['All Basic + Monthly/Yearly analytics', 'Graphs per patient', 'Exportable reports', 'Speech-based entry'],
    extra: '',
  },
  {
    name: 'Clinic Team License',
    price: '$149',
    period: '/month',
    features: ['Up to 5 professionals', 'Shared patient records', 'Role-based access', 'Admin control'],
    extra: '',
  },
];

const SCHOOL_PLANS = [
  {
    name: 'Education Starter (small schools)',
    price: '$49',
    period: '/month',
    features: ['Emotional check-ins', 'Weekly reports for students'],
    extra: '',
  },
  {
    name: 'Academic Wellness (universities)',
    price: '$149',
    period: '/month',
    features: ['Class-wise emotion graphs', 'Student mental health states', 'AI-based recommendations'],
    extra: '',
  },
  {
    name: 'Institution Plan',
    price: 'Custom',
    period: '(e.g. $299–$499/month)',
    features: ['Large-scale integration', 'Department reports', 'Annual trend analysis', 'Family dashboard access'],
    extra: '',
  },
];

const SPORTS_PLANS = [
  {
    name: 'Coach Lite',
    price: '$49',
    period: '/month',
    features: ['Athlete mental status tracker', 'Pre/post game reports'],
    extra: '',
  },
  {
    name: 'Pro Sports Plan',
    price: '$99',
    period: '/month',
    features: ['Emotional state prediction', 'Performance + stress correlation', 'Athlete mental dashboard'],
    extra: '',
  },
  {
    name: 'Elite Plan',
    price: '$199',
    period: '/month',
    features: ['Team-level analytics', 'Doctor collaboration', 'Longitudinal performance trends'],
    extra: '',
  },
];

const SPORTS_ADDONS = [
  'Data Export / PDF Report→ +$5/month',
  'Multi-language Support→ +$3/month',
  'Zero-Knowledge Proof & Blockchain Encryption→ +$7/month (Premium security layer)',
];

const ADDONS_PLANS = [
  {
    name: 'Add-ons for All Plans',
    price: '',
    period: '',
    features: SPORTS_ADDONS,
    extra: '',
  },
];

function TabIcon({ icon }) {
  switch (icon) {
    case 'user':
      return (
        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" style={{ marginRight: 6, verticalAlign: 'middle' }}>
          <circle cx="12" cy="8" r="4.5" stroke="currentColor" strokeWidth="2" />
          <path d="M4.5 19.5c0-3.5 3.5-6 7.5-6s7.5 2.5 7.5 6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
        </svg>
      );
    case 'doctor':
      return (
        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" style={{ marginRight: 6, verticalAlign: 'middle' }}>
          <rect x="7" y="2" width="10" height="6" rx="2" stroke="currentColor" strokeWidth="2" />
          <rect x="3" y="8" width="18" height="14" rx="4" stroke="currentColor" strokeWidth="2" />
          <path d="M12 12v4M10 14h4" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
        </svg>
      );
    case 'corporate':
      return (
        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" style={{ marginRight: 6, verticalAlign: 'middle' }}>
          <rect x="3" y="10" width="6" height="10" rx="2" stroke="currentColor" strokeWidth="2" />
          <rect x="9" y="4" width="6" height="16" rx="2" stroke="currentColor" strokeWidth="2" />
          <rect x="15" y="7" width="6" height="13" rx="2" stroke="currentColor" strokeWidth="2" />
        </svg>
      );
    case 'school':
      return (
        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" style={{ marginRight: 6, verticalAlign: 'middle' }}>
          <path d="M3 10l9-5 9 5-9 5-9-5z" stroke="currentColor" strokeWidth="2" />
          <path d="M21 10v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-6" stroke="currentColor" strokeWidth="2" />
        </svg>
      );
    case 'basketball':
      return (
        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" style={{ marginRight: 6, verticalAlign: 'middle' }}>
          <circle cx="12" cy="12" r="9" stroke="currentColor" strokeWidth="2" />
          <path d="M12 3v18M3 12h18" stroke="currentColor" strokeWidth="2" />
          <path d="M5.6 5.6c4.8 4.8 8 8 12.8 12.8" stroke="currentColor" strokeWidth="2" />
          <path d="M18.4 5.6C13.6 10.4 10.4 13.6 5.6 18.4" stroke="currentColor" strokeWidth="2" />
        </svg>
      );
    case 'plus':
      return (
        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" style={{ marginRight: 6, verticalAlign: 'middle' }}>
          <circle cx="12" cy="12" r="9" stroke="currentColor" strokeWidth="2" />
          <path d="M12 8v8M8 12h8" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
        </svg>
      );
    default:
      return null;
  }
}

function PricingCard({ plan, idx, onMouseEnter, onMouseLeave, onClick, onKeyDown, selected, hovered, t }) {
  const x = useMotionValue(0);
  const y = useMotionValue(0);

  const rotateX = useTransform(y, [-100, 100], [5, -5]);
  const rotateY = useTransform(x, [-100, 100], [-5, 5]);

  function handleMouse(event) {
    const rect = event.currentTarget.getBoundingClientRect();
    const centerX = rect.left + rect.width / 2;
    const centerY = rect.top + rect.height / 2;
    x.set(event.clientX - centerX);
    y.set(event.clientY - centerY);
  }

  function resetMouse() {
    x.set(0);
    y.set(0);
  }

  return (
    <motion.div
      className={`pricing-card${selected ? ' selected' : ''}${hovered ? ' hovered' : ''}`}
      role="button"
      tabIndex={0}
      aria-pressed={selected}
      onClick={onClick}
      onMouseEnter={onMouseEnter}
      onMouseLeave={() => {
        onMouseLeave();
        resetMouse();
      }}
      onMouseMove={handleMouse}
      onKeyDown={onKeyDown}
      style={{
        rotateX,
        rotateY,
        transformStyle: 'preserve-3d',
      }}
      initial={{ opacity: 0, y: 50, rotateX: -10 }}
      animate={{ opacity: 1, y: 0, rotateX: 0 }}
      transition={{
        delay: idx * 0.12,
        duration: 0.6,
        type: "spring",
        stiffness: 100,
        damping: 15
      }}
      whileHover={{
        y: -12,
        scale: 1.03,
        boxShadow: "0 20px 40px rgba(255, 145, 0, 0.4)",
        transition: { duration: 0.2 }
      }}
      whileTap={{ scale: 0.97 }}
    >
      <motion.div
        style={{ transform: 'translateZ(30px)' }}
        initial={{ opacity: 0, scale: 0.9 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ delay: idx * 0.12 + 0.2, duration: 0.4 }}
      >
        <div className="plan-title">{plan.name}</div>
      </motion.div>

      <motion.div
        className="plan-price-row"
        style={{ transform: 'translateZ(40px)' }}
        initial={{ opacity: 0, scale: 0.7 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{
          delay: idx * 0.12 + 0.3,
          type: "spring",
          stiffness: 300,
          damping: 20
        }}
      >
        {plan.name === t('Premium Plan') ? (
          <span
            className="plan-price"
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 8,
              flexWrap: 'wrap',
              color: '#fff',
              fontSize: '2.1rem',
              fontWeight: 700,
              justifyContent: 'center',
              width: '100%',
              textAlign: 'center',
            }}
          >
            <span style={{ marginRight: 6 }}>
              $100 <span className="plan-period" style={{ color: '#fff', fontSize: '1.1rem', fontWeight: 500 }}>/ year</span>
            </span>
            <span className="plan-period" style={{ color: '#fff', fontSize: '1.1rem', fontWeight: 500, margin: '0 6px' }}>
              or
            </span>
            <span style={{ color: '#fff', fontWeight: 700 }}>
              $10 <span className="plan-period" style={{ color: '#fff', fontSize: '1.1rem', fontWeight: 500 }}>/ month</span>
            </span>
          </span>
        ) : (
          <>
            <span className="plan-price">{plan.price}</span>
            {plan.period && <span className="plan-period">{plan.period}</span>}
          </>
        )}
      </motion.div>

      <motion.ul
        className="plan-features"
        style={{ transform: 'translateZ(20px)' }}
      >
        {plan.features.map((f, fIdx) => (
          <motion.li
            key={f}
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{
              delay: idx * 0.12 + 0.4 + fIdx * 0.08,
              type: "spring",
              stiffness: 200
            }}
            whileHover={{ x: 5, color: "#ff9100" }}
          >
            {f}
          </motion.li>
        ))}
      </motion.ul>

      <motion.div
        style={{
          position: 'absolute',
          inset: 0,
          background: 'linear-gradient(135deg, rgba(255,145,0,0.1) 0%, rgba(255,145,0,0) 50%)',
          borderRadius: 'inherit',
          pointerEvents: 'none',
          opacity: 0,
          transform: 'translateZ(10px)',
        }}
        animate={{
          opacity: hovered ? 1 : 0,
        }}
        transition={{ duration: 0.3 }}
      />
    </motion.div>
  );
}

export { PLANS, DOCTOR_PLANS, CORPORATE_PLANS, SCHOOL_PLANS, SPORTS_PLANS };

export default function PricingSection() {
  const [activeTab, setActiveTab] = useState(() => window.innerWidth < 768 ? null : 'individual');
  const sectionRef = useRef(null);
  const [visible, setVisible] = useState(false);
  const [selected, setSelected] = useState(null);
  const [hovered, setHovered] = useState(null);
  const tabsRef = useRef(null);
  const [showArrow, setShowArrow] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [roleIdx, setRoleIdx] = useState(0);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  const [slideDir, setSlideDir] = useState(null);
  const { t } = useTranslation();
  const [paymentOpen, setPaymentOpen] = useState(false);
  const [paymentPlanName, setPaymentPlanName] = useState("");
  const [paymentAmount, setPaymentAmount] = useState(null);
  const [paymentPaypalLink, setPaymentPaypalLink] = useState(null);
  const [stripeLoading, setStripeLoading] = useState(false);

  useEffect(() => {
    function handleResize() {
      setIsMobile(window.innerWidth < 768);
    }
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    if (isMobile) {
      setActiveTab(TABS[roleIdx].key);
    }
  }, [roleIdx, isMobile]);

  useEffect(() => {
    if (!isMobile && !activeTab) {
      setActiveTab('individual');
    }
  }, [isMobile, activeTab]);

  useEffect(() => {
    const observer = new window.IntersectionObserver(
      ([entry]) => setVisible(entry.isIntersecting),
      { threshold: 0.2 }
    );
    if (sectionRef.current) observer.observe(sectionRef.current);
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    if (tabsRef.current) {
      tabsRef.current.scrollLeft = 0;
    }
  }, []);

  useEffect(() => {
    function checkTabsOverflow() {
      if (!tabsRef.current) return;
      setShowArrow(tabsRef.current.scrollWidth > tabsRef.current.clientWidth + 2 && !scrolled);
    }
    checkTabsOverflow();
    window.addEventListener('resize', checkTabsOverflow);
    return () => window.removeEventListener('resize', checkTabsOverflow);
  }, [scrolled]);

  function handleArrowClick() {
    if (tabsRef.current) {
      const lastTab = tabsRef.current.children[tabsRef.current.children.length - 1];
      const containerRect = tabsRef.current.getBoundingClientRect();
      const lastTabRect = lastTab.getBoundingClientRect();
      const scrollAmount = lastTabRect.right - containerRect.right + lastTab.offsetWidth * 0.1;
      tabsRef.current.scrollBy({ left: scrollAmount, behavior: 'smooth' });
      setScrolled(true);
      setShowArrow(false);
    }
  }

  function handleMobileArrow(dir) {
    setSlideDir(dir);
    setTimeout(() => {
      setRoleIdx(idx => {
        if (dir === 'right') return Math.min(idx + 1, TABS.length - 1);
        if (dir === 'left') return Math.max(idx - 1, 0);
        return idx;
      });
      setSlideDir(null);
    }, 180);
  }

  function handleCardKeyDown(idx, e) {
    if (e.key === 'Enter' || e.key === ' ') {
      setSelected(idx);
    }
  }

  const TABS_T = [
    { label: t('Individual'), icon: 'user', key: 'individual' },
    { label: t('Doctor'), icon: 'doctor', key: 'doctor' },
    { label: t('Corporate'), icon: 'corporate', key: 'corporate' },
    { label: t('School / University'), icon: 'school', key: 'school' },
    { label: t('Sports Management'), icon: 'basketball', key: 'sports' },
    { label: t('Add-ons for All Plans'), icon: 'plus', key: 'addons' },
  ];

  function translatePlan(plan) {
    return {
      ...plan,
      name: t(plan.name),
      price: t(plan.price),
      period: plan.period ? t(plan.period) : '',
      features: plan.features.map(f => t(f)),
      extra: plan.extra ? t(plan.extra) : '',
    };
  }

  const PLANS_T = PLANS.map(translatePlan);
  const DOCTOR_PLANS_T = DOCTOR_PLANS.map(translatePlan);
  const CORPORATE_PLANS_T = CORPORATE_PLANS.map(translatePlan);
  const SCHOOL_PLANS_T = SCHOOL_PLANS.map(translatePlan);
  const SPORTS_PLANS_T = SPORTS_PLANS.map(translatePlan);
  const ADDONS_PLANS_T = ADDONS_PLANS.map(translatePlan);

  const showCards = !activeTab || activeTab === 'individual' || activeTab === 'doctor' || activeTab === 'corporate' || activeTab === 'school' || activeTab === 'sports' || activeTab === 'addons';
  const plansToShow = activeTab === 'doctor' ? DOCTOR_PLANS_T : activeTab === 'corporate' ? CORPORATE_PLANS_T : activeTab === 'school' ? SCHOOL_PLANS_T : activeTab === 'sports' ? SPORTS_PLANS_T : activeTab === 'addons' ? ADDONS_PLANS_T : PLANS_T;

  function parseAmountFromPlan(p) {
    if (p.name === t('Free Plan')) return 0;
    if (p.name === t('Premium Plan')) return 10;
    if (p.name === t('Companion AI')) return 25;
    if (p.name === t('Donation Option')) return null;
    const priceStr = String(p.price || '').replace(/,/g, '');
    const match = priceStr.match(/(\d+(?:\.\d+)?)/);
    return match ? Number(match[1]) : null;
  }

  function openPaymentModal(plan) {
    const amt = parseAmountFromPlan(plan);
    setPaymentAmount(amt);
    setPaymentPlanName(plan.name);
    
    let paypalLink = null;
    if (plan.name === t('Premium Plan') || plan.name === t('Companion AI') || plan.name === t('Donation Option')) {
      paypalLink = true;
    }
    
    if (plan.name === t('Free Plan')) {
      setPaymentPaypalLink(null);
    } else {
      setPaymentPaypalLink(paypalLink);
    }
    setPaymentOpen(true);
  }

  return (
    <motion.section
      className={`pricing-section${visible ? ' visible' : ''}`}
      ref={sectionRef}
      id="pricing"
      initial={{ opacity: 0 }}
      animate={{ opacity: visible ? 1 : 0 }}
      transition={{ duration: 0.8, ease: [0.6, 0.05, 0.01, 0.9] }}
    >
      <motion.div
        className="pricing-tabs-row"
        style={{ position: 'relative' }}
        initial={{ y: -30, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ delay: 0.3, duration: 0.7, type: "spring", stiffness: 100 }}
      >
        {isMobile ? (
          <div className="pricing-tabs-mobile-carousel">
            {roleIdx > 0 && (
              <motion.button
                className="pricing-tabs-arrow mobile-left"
                onClick={() => handleMobileArrow('left')}
                aria-label={t('Previous role')}
                tabIndex={0}
                style={{ left: 0, zIndex: 2 }}
                whileHover={{ scale: 1.15, x: -3 }}
                whileTap={{ scale: 0.9 }}
                animate={{ x: [-2, 0, -2] }}
                transition={{ x: { repeat: Infinity, duration: 2 } }}
              >
                <span style={{ fontSize: '1.5rem', color: '#ff9100', background: 'rgba(24,24,24,0.95)', borderRadius: '50%', padding: '0.2rem 0.5rem', boxShadow: '0 2px 8px #0006', border: 'none' }}>←</span>
              </motion.button>
            )}
            <AnimatePresence mode="wait">
              <motion.div
                key={TABS_T[roleIdx].key}
                className="pricing-tabs-mobile-slide"
                style={{ display: 'inline-block', width: 'calc(100vw - 100px)', maxWidth: 320, textAlign: 'center', verticalAlign: 'middle' }}
                initial={{ opacity: 0, scale: 0.8, rotateY: slideDir === 'right' ? 90 : -90 }}
                animate={{ opacity: 1, scale: 1, rotateY: 0 }}
                exit={{ opacity: 0, scale: 0.8, rotateY: slideDir === 'right' ? -90 : 90 }}
                transition={{ duration: 0.4, type: "spring", stiffness: 200 }}
              >
                <button
                  className={`pricing-tab${activeTab === TABS_T[roleIdx].key ? ' active' : ''}`}
                  onClick={() => setActiveTab(TABS_T[roleIdx].key)}
                  type="button"
                  style={{ marginRight: 0, minWidth: 120 }}
                >
                  <TabIcon icon={TABS_T[roleIdx].icon} />
                  {TABS_T[roleIdx].label}
                </button>
              </motion.div>
            </AnimatePresence>
            {roleIdx < TABS_T.length - 1 && (
              <motion.button
                className="pricing-tabs-arrow mobile-right"
                onClick={() => handleMobileArrow('right')}
                aria-label={t('Next role')}
                tabIndex={0}
                style={{ right: 0, zIndex: 2 }}
                whileHover={{ scale: 1.15, x: 3 }}
                whileTap={{ scale: 0.9 }}
                animate={{ x: [0, 2, 0] }}
                transition={{ x: { repeat: Infinity, duration: 2 } }}
              >
                <span style={{ fontSize: '1.5rem', color: '#ff9100', background: 'rgba(24,24,24,0.95)', borderRadius: '50%', padding: '0.2rem 0.5rem', boxShadow: '0 2px 8px #0006', border: 'none' }}>→</span>
              </motion.button>
            )}
          </div>
        ) : (
          <>
            <div className="pricing-tabs" ref={tabsRef}>
              {TABS_T.map((tab, i) => (
                <motion.button
                  key={tab.key}
                  className={`pricing-tab${activeTab === tab.key ? ' active' : ''}`}
                  onClick={() => setActiveTab(tab.key)}
                  type="button"
                  style={{ marginRight: i !== TABS_T.length - 1 ? '1rem' : 0 }}
                  whileHover={{
                    scale: 1.08,
                    y: -4,
                    boxShadow: "0 8px 20px rgba(255, 145, 0, 0.3)"
                  }}
                  whileTap={{ scale: 0.95 }}
                  initial={{ opacity: 0, y: -15, rotateX: -20 }}
                  animate={{ opacity: 1, y: 0, rotateX: 0 }}
                  transition={{
                    delay: i * 0.08,
                    duration: 0.5,
                    type: "spring",
                    stiffness: 150
                  }}
                >
                  <motion.div
                    animate={activeTab === tab.key ? { rotate: [0, 10, -10, 0] } : {}}
                    transition={{ duration: 0.5 }}
                  >
                    <TabIcon icon={tab.icon} />
                  </motion.div>
                  {tab.label}
                </motion.button>
              ))}
            </div>
            {showArrow && (
              <motion.button
                className="pricing-tabs-arrow"
                onClick={handleArrowClick}
                aria-label={t('Scroll tabs right')}
                tabIndex={0}
                style={{ position: 'absolute', right: 0, top: '50%', transform: 'translateY(-50%)', zIndex: 2 }}
                initial={{ opacity: 0, scale: 0 }}
                animate={{
                  opacity: 1,
                  scale: 1,
                  x: [0, 5, 0]
                }}
                transition={{
                  x: { repeat: Infinity, duration: 1.2, ease: "easeInOut" },
                  scale: { duration: 0.3 }
                }}
                whileHover={{ scale: 1.2, rotate: 15 }}
                whileTap={{ scale: 0.9 }}
              >
                <span style={{ fontSize: '1.5rem', color: '#ff9100', background: 'rgba(24,24,24,0.95)', borderRadius: '50%', padding: '0.2rem 0.5rem', boxShadow: '0 2px 8px #0006', border: 'none' }}>→</span>
              </motion.button>
            )}
          </>
        )}
      </motion.div>

      {showCards ? (
        <AnimatePresence mode="wait">
          <motion.div
            key={activeTab}
            className="pricing-cards-row"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            transition={{ duration: 0.4, staggerChildren: 0.1 }}
          >
            {plansToShow.map((plan, idx) => (
              <PricingCard
                key={plan.name}
                plan={plan}
                idx={idx}
                selected={selected === idx}
                hovered={hovered === idx}
                onMouseEnter={() => setHovered(idx)}
                onMouseLeave={() => setHovered(null)}
                onClick={() => openPaymentModal(plan)}
                onKeyDown={(e) => handleCardKeyDown(idx, e)}
                t={t}
              />
            ))}
          </motion.div>
        </AnimatePresence>
      ) : (
        <motion.div
          className="pricing-sales-message"
          initial={{ opacity: 0, scale: 0.8, rotateX: -15 }}
          animate={{ opacity: 1, scale: 1, rotateX: 0 }}
          transition={{ duration: 0.6, type: "spring", stiffness: 120 }}
        >
          <motion.div
            className="sales-message-card"
            whileHover={{
              scale: 1.08,
              boxShadow: "0 12px 50px rgba(0,0,0,0.4)",
              rotateY: 5
            }}
            transition={{ type: "spring", stiffness: 300 }}
          >
            <motion.div
              className="sales-message-title"
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 }}
            >
              {t('Contact the Neurolonic sales team at')}
            </motion.div>
            <motion.a
              href="mailto:Oracle@Neurolonic.com"
              className="sales-message-email"
              target="_blank"
              rel="noopener noreferrer"
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3 }}
              whileHover={{
                scale: 1.15,
                textShadow: "0 0 12px rgba(255, 145, 0, 0.8)",
                y: -3
              }}
              whileTap={{ scale: 0.95 }}
            >
              Oracle@Neurolonic.com
            </motion.a>
          </motion.div>
        </motion.div>
      )}

      <AnimatePresence>
  {paymentOpen && (
    <motion.div 
      className="payment-modal-overlay"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      onClick={() => setPaymentOpen(false)}
    >
      <motion.div 
        className="payment-modal-card"
        initial={{ scale: 0.8, y: 50, opacity: 0 }}
        animate={{ scale: 1, y: 0, opacity: 1 }}
        exit={{ scale: 0.8, y: 50, opacity: 0 }}
        transition={{ type: "spring", stiffness: 300, damping: 25 }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header with Gradient */}
        <div className="payment-modal-header">
          <motion.button
            className="payment-modal-close"
            onClick={() => setPaymentOpen(false)}
            whileHover={{ scale: 1.1, rotate: 90 }}
            whileTap={{ scale: 0.9 }}
          >
            ×
          </motion.button>

          <motion.div
            className="payment-modal-icon"
            initial={{ scale: 0, rotate: -180 }}
            animate={{ scale: 1, rotate: 0 }}
            transition={{ delay: 0.2, type: "spring", stiffness: 200 }}
          >
            <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2">
              <rect x="2" y="6" width="20" height="14" rx="2"/>
              <path d="M2 10h20"/>
              <rect x="6" y="14" width="4" height="2" rx="1" fill="white"/>
            </svg>
          </motion.div>

          <motion.h2
            className="payment-modal-title"
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
          >
            {paymentPlanName === t('Free Plan') ? t('Support Neurolonic') : t('Choose Payment Method')}
          </motion.h2>

          <motion.div
            style={{ textAlign: 'center' }}
            initial={{ opacity: 0, scale: 0.8 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ delay: 0.2 }}
          >
            <span className="payment-modal-badge">{paymentPlanName}</span>
          </motion.div>
        </div>

        {/* Body */}
        <div className="payment-modal-body">
          <motion.div
            className="payment-amount-card"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
          >
            {paymentPlanName === t('Free Plan') ? (
              <>
                <div className="payment-amount-label">
                  {t('Make a donation or upgrade to Premium')}
                </div>
                <div className="payment-amount-value">{t('Custom Amount')}</div>
              </>
            ) : (
              <>
                <div className="payment-amount-label">{t('Total Amount')}</div>
                <div className="payment-amount-value">
                  ${paymentAmount !== null ? paymentAmount : 'Custom'}
                </div>
              </>
            )}
          </motion.div>

          <div className="payment-buttons">
            {paymentPaypalLink && (
              <motion.button
                className="payment-btn payment-btn-paypal"
                onClick={async () => {
                  try {
                    const userId = localStorage.getItem('neuro_user_id');
                    if (!userId) {
                      alert('Please login to continue.');
                      return;
                    }
                    const amount = paymentAmount;
                    const response = await createPayPalPayment(userId, amount);
                    if (response && response.checkout_url) {
                      window.open(response.checkout_url, '_blank', 'noopener,noreferrer');
                    } else {
                      alert('Failed to create PayPal payment.');
                    }
                  } catch (error) {
                    console.error('PayPal payment error:', error);
                    alert('Failed to create PayPal payment.');
                  }
                }}
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.4 }}
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
              >
                <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M7.076 21.337H2.47a.641.641 0 0 1-.633-.74L4.944.901C5.026.382 5.474 0 5.998 0h7.46c2.57 0 4.578.543 5.69 1.81 1.01 1.15 1.304 2.42 1.012 4.287-.023.143-.047.288-.077.437-.983 5.05-4.349 6.797-8.647 6.797h-2.19c-.524 0-.968.382-1.05.9l-1.12 7.106zm14.146-14.42a3.35 3.35 0 0 0-.607-.541c-.013.076-.026.175-.041.254-.93 4.778-4.005 7.201-9.138 7.201h-2.19a.563.563 0 0 0-.556.479l-1.187 7.527h-.506l.506-3.219 1.122-7.108c.082-.517.526-.899 1.05-.899h2.19c4.298 0 7.664-1.747 8.647-6.797.03-.149.054-.294.077-.437.201-1.28.072-2.26-.367-3.04z"/>
                </svg>
                {t('PAY WITH PAYPAL')}
              </motion.button>
            )}

            <motion.button
              className="payment-btn payment-btn-stripe"
              disabled={stripeLoading}
              onClick={async () => {
                try {
                  let amount = paymentAmount;
                  if (amount === null) {
                    const input = window.prompt('Enter amount in USD');
                    if (!input) return;
                    const parsed = Number(String(input).replace(/[^\d.]/g, ''));
                    if (!parsed || parsed <= 0) {
                      alert('Invalid amount');
                      return;
                    }
                    amount = parsed;
                  }
                  const userId = localStorage.getItem('neuro_user_id');
                  if (!userId) {
                    alert('Please login to continue.');
                    return;
                  }
                  setStripeLoading(true);
                  const url = `https://app.neurolonic.com/api/payment/stripe?user_id=${encodeURIComponent(userId)}&amount=${encodeURIComponent(amount)}`;
                  const res = await fetch(url, { method: 'POST' });
                  const data = await res.json().catch(() => ({}));
                  const checkoutUrl = data && (data.checkout_url || data.url);
                  if (checkoutUrl) {
                    window.location.href = checkoutUrl;
                  } else {
                    alert('Failed to start Stripe checkout.');
                  }
                } catch (e) {
                  alert('Failed to start Stripe checkout.');
                } finally {
                  setStripeLoading(false);
                }
              }}
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: paymentPaypalLink ? 0.5 : 0.4 }}
              whileHover={!stripeLoading ? { scale: 1.02 } : {}}
              whileTap={!stripeLoading ? { scale: 0.98 } : {}}
            >
              {stripeLoading ? (
                <>
                  <div className="payment-loading-spinner" />
                  {t('Processing...')}
                </>
              ) : (
                <>
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M13.976 9.15c-2.172-.806-3.356-1.426-3.356-2.409 0-.831.683-1.305 1.901-1.305 2.227 0 4.515.858 6.09 1.631l.89-5.494C18.252.975 15.697 0 12.165 0 9.667 0 7.589.654 6.104 1.872 4.56 3.147 3.757 4.992 3.757 7.218c0 4.039 2.467 5.76 6.476 7.219 2.585.92 3.445 1.574 3.445 2.583 0 .98-.84 1.545-2.354 1.545-1.875 0-4.965-.921-6.99-2.109l-.9 5.555C5.175 22.99 8.385 24 11.714 24c2.641 0 4.843-.624 6.328-1.813 1.664-1.305 2.525-3.236 2.525-5.732 0-4.128-2.524-5.851-6.594-7.305h.003z"/>
                  </svg>
                  {t('PAY WITH STRIPE')}
                </>
              )}
            </motion.button>

            <motion.button
              className="payment-btn payment-btn-cancel"
              onClick={() => setPaymentOpen(false)}
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: paymentPaypalLink ? 0.6 : 0.5 }}
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
            >
              {t('Cancel')}
            </motion.button>
          </div>

          <motion.div
            className="payment-security-badge"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.7 }}
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <rect x="3" y="11" width="18" height="11" rx="2" ry="2"/>
              <path d="M7 11V7a5 5 0 0 1 10 0v4"/>
            </svg>
            {t('Secure payment with SSL encryption')}
          </motion.div>
        </div>
      </motion.div>
    </motion.div>
  )}
</AnimatePresence>

    </motion.section>
  );
}
